import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { adminApi } from "@/lib/apiClient";
import { SETTINGS } from "@/constants/testIds";

const EMPTY_ENTITY = {
  id: "",
  legal_name: "",
  trade_name: "",
  trn: "",
  vat_registered: false,
  contact_email: "",
  contact_phone: "",
  address: { line: "", emirate: "", country: "AE" },
  invoice_number_prefix: "INV",
  trade_licence_number: "",
  licence_jurisdiction: "",
};

export default function AdminSettings() {
  const nav = useNavigate();
  const [entities, setEntities] = useState([]);
  const [entity, setEntity] = useState(EMPTY_ENTITY);
  const [entitySaving, setEntitySaving] = useState(false);
  const [entitySaved, setEntitySaved] = useState(false);
  const [entityError, setEntityError] = useState("");

  const [company, setCompany] = useState({
    trade_name: "",
    primary_contact_email: "",
    primary_contact_phone: "",
    whatsapp: "",
    business_hours: "",
    social: {},
  });
  const [companySaving, setCompanySaving] = useState(false);
  const [companySaved, setCompanySaved] = useState(false);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [ents, comp] = await Promise.all([
          adminApi.legalEntities(),
          adminApi.getSettings("company").catch(() => null),
        ]);
        if (!alive) return;
        setEntities(ents);
        if (ents.length > 0) hydrateEntity(ents[0]);
        if (comp) setCompany({ ...company, ...comp.value });
      } catch (e) {
        if (!alive) return;
        setPageError(e.response?.data?.error?.message || "Failed to load settings.");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const hydrateEntity = (e) => {
    setEntity({
      id: e.id,
      legal_name: e.legal_name || "",
      trade_name: e.trade_name || "",
      trn: e.trn || "",
      vat_registered: !!e.vat_registered,
      contact_email: e.contact_email || "",
      contact_phone: e.contact_phone || "",
      address: {
        line: e.address?.line || "",
        emirate: e.address?.emirate || "",
        country: e.address?.country || "AE",
      },
      invoice_number_prefix: e.invoice_number_prefix || "INV",
      trade_licence_number: e.trade_licence_number || "",
      licence_jurisdiction: e.licence_jurisdiction || "",
    });
  };

  const onSaveEntity = async (ev) => {
    ev.preventDefault();
    setEntityError("");
    setEntitySaved(false);
    setEntitySaving(true);
    try {
      const patch = {
        legal_name: entity.legal_name,
        trade_name: entity.trade_name || null,
        trn: entity.trn || null,
        vat_registered: entity.vat_registered,
        contact_email: entity.contact_email || null,
        contact_phone: entity.contact_phone || null,
        address: entity.address,
        invoice_number_prefix: entity.invoice_number_prefix,
        trade_licence_number: entity.trade_licence_number || null,
        licence_jurisdiction: entity.licence_jurisdiction || null,
      };
      const updated = await adminApi.updateLegalEntity(entity.id, patch);
      hydrateEntity(updated);
      setEntities((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      setEntitySaved(true);
    } catch (err) {
      setEntityError(err.response?.data?.error?.message || "Save failed.");
    } finally {
      setEntitySaving(false);
    }
  };

  const onSaveCompany = async (ev) => {
    ev.preventDefault();
    setCompanySaved(false);
    setCompanySaving(true);
    try {
      const saved = await adminApi.putSettings("company", company);
      setCompany({ ...company, ...saved.value });
      setCompanySaved(true);
    } catch (err) {
      setPageError(err.response?.data?.error?.message || "Save failed.");
    } finally {
      setCompanySaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid={SETTINGS.root}>
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              Admin &rsaquo; Settings
            </div>
            <div className="text-lg font-semibold">Legal entity &amp; company info</div>
          </div>
          <Button asChild variant="outline" data-testid={SETTINGS.navBack}>
            <Link to="/admin">&larr; Admin home</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {pageError && (
          <Alert variant="destructive">
            <AlertDescription>{pageError}</AlertDescription>
          </Alert>
        )}

        <div className="text-sm text-muted-foreground max-w-2xl">
          These values populate every invoice and public contact touchpoint.
          <span className="block mt-1">
            Any changes are audit-logged and require review by a qualified UAE tax
            adviser before invoices are considered final.
          </span>
        </div>

        <Tabs defaultValue="entity" className="w-full">
          <TabsList>
            <TabsTrigger value="entity" data-testid="settings-tab-entity">
              Legal entity
            </TabsTrigger>
            <TabsTrigger value="company" data-testid="settings-tab-company">
              Company info
            </TabsTrigger>
            <TabsTrigger value="delivery" data-testid="settings-tab-delivery">
              Delivery fees
            </TabsTrigger>
          </TabsList>

          <TabsContent value="entity" className="pt-6">
            {entities.length > 1 && (
              <div className="mb-4">
                <Label htmlFor="entity-select">Editing entity</Label>
                <select
                  id="entity-select"
                  data-testid={SETTINGS.entitySelect}
                  value={entity.id}
                  onChange={(e) => {
                    const found = entities.find((x) => x.id === e.target.value);
                    if (found) hydrateEntity(found);
                  }}
                  className="mt-1 block w-full border border-input rounded-md bg-transparent px-3 py-2 text-sm"
                >
                  {entities.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.legal_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Legal entity details</CardTitle>
                <CardDescription>
                  What appears on tax invoices. TRN must be 15 digits if the entity is
                  VAT-registered.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={onSaveEntity}
                  className="space-y-5"
                  data-testid={SETTINGS.entityForm}
                  noValidate
                >
                  {entitySaved && (
                    <Alert data-testid={SETTINGS.entitySavedAlert}>
                      <AlertDescription>Legal entity updated.</AlertDescription>
                    </Alert>
                  )}
                  {entityError && (
                    <Alert variant="destructive" data-testid={SETTINGS.entityErrorAlert}>
                      <AlertDescription>{entityError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="legal_name">Legal name (from trade licence)</Label>
                      <Input
                        id="legal_name"
                        value={entity.legal_name}
                        onChange={(e) => setEntity({ ...entity, legal_name: e.target.value })}
                        required
                        data-testid={SETTINGS.legalName}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="trade_name">Trade name (customer-facing)</Label>
                      <Input
                        id="trade_name"
                        value={entity.trade_name}
                        onChange={(e) => setEntity({ ...entity, trade_name: e.target.value })}
                        placeholder="e.g. Acme"
                        data-testid={SETTINGS.tradeName}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invoice_prefix">Invoice number prefix</Label>
                      <Input
                        id="invoice_prefix"
                        value={entity.invoice_number_prefix}
                        onChange={(e) =>
                          setEntity({ ...entity, invoice_number_prefix: e.target.value })
                        }
                        maxLength={8}
                        data-testid={SETTINGS.invoicePrefix}
                      />
                    </div>

                    <Separator className="sm:col-span-2" />

                    <div className="space-y-2">
                      <Label htmlFor="trn">TRN (15 digits)</Label>
                      <Input
                        id="trn"
                        value={entity.trn}
                        onChange={(e) => setEntity({ ...entity, trn: e.target.value })}
                        placeholder="15-digit number"
                        inputMode="numeric"
                        data-testid={SETTINGS.trn}
                      />
                      <p className="text-xs text-muted-foreground">
                        Leave blank if the entity is not VAT-registered.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vat_registered">VAT registered</Label>
                      <div className="flex items-center gap-2 h-10">
                        <Switch
                          id="vat_registered"
                          checked={entity.vat_registered}
                          onCheckedChange={(v) => setEntity({ ...entity, vat_registered: v })}
                          data-testid={SETTINGS.vatRegistered}
                        />
                        <span className="text-sm text-muted-foreground">
                          {entity.vat_registered ? "Yes" : "No"}
                        </span>
                      </div>
                    </div>

                    <Separator className="sm:col-span-2" />

                    <div className="space-y-2">
                      <Label htmlFor="contact_email">Entity contact email</Label>
                      <Input
                        id="contact_email"
                        type="email"
                        value={entity.contact_email}
                        onChange={(e) => setEntity({ ...entity, contact_email: e.target.value })}
                        data-testid={SETTINGS.contactEmail}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_phone">Entity contact phone</Label>
                      <Input
                        id="contact_phone"
                        value={entity.contact_phone}
                        onChange={(e) => setEntity({ ...entity, contact_phone: e.target.value })}
                        placeholder="+9714…"
                        data-testid={SETTINGS.contactPhone}
                      />
                    </div>

                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="addr_line">Address (building, area)</Label>
                      <Input
                        id="addr_line"
                        value={entity.address.line}
                        onChange={(e) =>
                          setEntity({
                            ...entity,
                            address: { ...entity.address, line: e.target.value },
                          })
                        }
                        data-testid={SETTINGS.addressLine}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="addr_emirate">Emirate</Label>
                      <Input
                        id="addr_emirate"
                        value={entity.address.emirate}
                        onChange={(e) =>
                          setEntity({
                            ...entity,
                            address: { ...entity.address, emirate: e.target.value },
                          })
                        }
                        placeholder="Dubai / Abu Dhabi / …"
                        data-testid={SETTINGS.addressEmirate}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="addr_country">Country</Label>
                      <Input
                        id="addr_country"
                        value={entity.address.country}
                        onChange={(e) =>
                          setEntity({
                            ...entity,
                            address: { ...entity.address, country: e.target.value },
                          })
                        }
                      />
                    </div>

                    <Separator className="sm:col-span-2" />

                    <div className="space-y-2">
                      <Label htmlFor="lic_no">Trade licence number</Label>
                      <Input
                        id="lic_no"
                        value={entity.trade_licence_number}
                        onChange={(e) =>
                          setEntity({ ...entity, trade_licence_number: e.target.value })
                        }
                        data-testid={SETTINGS.licenceNumber}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lic_jur">Licence jurisdiction</Label>
                      <Input
                        id="lic_jur"
                        value={entity.licence_jurisdiction}
                        onChange={(e) =>
                          setEntity({ ...entity, licence_jurisdiction: e.target.value })
                        }
                        placeholder="e.g. Dubai DED / DMCC / RAKEZ"
                        data-testid={SETTINGS.licenceJurisdiction}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      type="submit"
                      disabled={entitySaving || !entity.id}
                      data-testid={SETTINGS.entitySaveBtn}
                    >
                      {entitySaving ? "Saving…" : "Save entity"}
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Changes are audit-logged.
                    </span>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="company" className="pt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Public company contact</CardTitle>
                <CardDescription>
                  Appears on the website and in customer notifications.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={onSaveCompany}
                  className="space-y-4"
                  data-testid={SETTINGS.companyForm}
                  noValidate
                >
                  {companySaved && (
                    <Alert data-testid={SETTINGS.companySavedAlert}>
                      <AlertDescription>Company info updated.</AlertDescription>
                    </Alert>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="c_trade">Public trade name</Label>
                      <Input
                        id="c_trade"
                        value={company.trade_name || ""}
                        onChange={(e) => setCompany({ ...company, trade_name: e.target.value })}
                        data-testid={SETTINGS.companyTradeName}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="c_email">Primary contact email</Label>
                      <Input
                        id="c_email"
                        type="email"
                        value={company.primary_contact_email || ""}
                        onChange={(e) =>
                          setCompany({ ...company, primary_contact_email: e.target.value })
                        }
                        data-testid={SETTINGS.companyContactEmail}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="c_phone">Primary contact phone</Label>
                      <Input
                        id="c_phone"
                        value={company.primary_contact_phone || ""}
                        onChange={(e) =>
                          setCompany({ ...company, primary_contact_phone: e.target.value })
                        }
                        data-testid={SETTINGS.companyContactPhone}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="c_wa">WhatsApp number</Label>
                      <Input
                        id="c_wa"
                        value={company.whatsapp || ""}
                        onChange={(e) => setCompany({ ...company, whatsapp: e.target.value })}
                        placeholder="+9715…"
                        data-testid={SETTINGS.companyWhatsapp}
                      />
                      <p className="text-xs text-muted-foreground">
                        Used by the customer <b>Inquiry / Negotiate</b> button on every product page.
                        Include the country code (e.g. +9715…).
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="c_sales">Salesman / contact name</Label>
                      <Input
                        id="c_sales"
                        value={company.salesman_name || ""}
                        onChange={(e) => setCompany({ ...company, salesman_name: e.target.value })}
                        placeholder="e.g. Ahmed"
                        data-testid="settings-company-salesman"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="c_inq_msg">Default inquiry message</Label>
                      <Input
                        id="c_inq_msg"
                        value={company.inquiry_default_message || ""}
                        onChange={(e) => setCompany({ ...company, inquiry_default_message: e.target.value })}
                        placeholder="Hello, I would like to inquire about this product."
                        data-testid="settings-company-inquiry-message"
                      />
                      <p className="text-xs text-muted-foreground">
                        Pre-filled first line when a customer opens WhatsApp. Product name and SKU are appended automatically.
                      </p>
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="c_notify_email">Send new-order alerts to</Label>
                      <Input
                        id="c_notify_email"
                        type="email"
                        value={company.order_notification_email || ""}
                        onChange={(e) =>
                          setCompany({ ...company, order_notification_email: e.target.value })
                        }
                        placeholder="you@company.com"
                        data-testid="settings-company-order-email"
                      />
                      <p className="text-xs text-muted-foreground">
                        Every time a customer places a Cash-on-Delivery order, we&rsquo;ll email a full order summary to this address. Leave blank to turn off.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="c_hours">Business hours</Label>
                      <Input
                        id="c_hours"
                        value={company.business_hours || ""}
                        onChange={(e) =>
                          setCompany({ ...company, business_hours: e.target.value })
                        }
                        placeholder="Sun–Thu 09:00–18:00"
                        data-testid={SETTINGS.companyHours}
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={companySaving}
                    data-testid={SETTINGS.companySaveBtn}
                  >
                    {companySaving ? "Saving…" : "Save company info"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="delivery" className="pt-6">
            <DeliveryTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const EMIRATES = [
  { key: "dubai", label: "Dubai" },
  { key: "abu_dhabi", label: "Abu Dhabi" },
  { key: "sharjah", label: "Sharjah" },
  { key: "ajman", label: "Ajman" },
  { key: "umm_al_quwain", label: "Umm Al Quwain" },
  { key: "ras_al_khaimah", label: "Ras Al Khaimah" },
  { key: "fujairah", label: "Fujairah" },
];

function DeliveryTab() {
  const [fees, setFees] = useState(() =>
    Object.fromEntries(EMIRATES.map((e) => [e.key, ""])),
  );
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    adminApi
      .getSettings("delivery")
      .then((d) => {
        if (!alive) return;
        const stored = (d.value && d.value.fees) || {};
        setFees(() =>
          Object.fromEntries(
            EMIRATES.map((e) => [
              e.key,
              stored[e.key] != null ? (Number(stored[e.key]) / 100).toFixed(2) : "",
            ]),
          ),
        );
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const submit = async (ev) => {
    ev.preventDefault();
    setErr("");
    setSaved(false);
    setBusy(true);
    try {
      const feesOut = {};
      for (const e of EMIRATES) {
        const v = fees[e.key];
        if (v === "" || v == null) continue;
        const num = Number(v);
        if (Number.isNaN(num) || num < 0) throw new Error(`Invalid fee for ${e.label}`);
        feesOut[e.key] = Math.round(num * 100);
      }
      await adminApi.putSettings("delivery", { currency: "AED", fees: feesOut });
      setSaved(true);
    } catch (e) {
      setErr(e.response?.data?.error?.message || e.message || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Delivery charges by emirate</CardTitle>
        <CardDescription>
          Set the fee (in AED) added to the customer&apos;s order based on their delivery
          emirate. Leave blank for free delivery.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <form
            onSubmit={submit}
            className="space-y-4"
            data-testid="settings-delivery-form"
            noValidate
          >
            {saved && (
              <Alert data-testid="settings-delivery-saved">
                <AlertDescription>Delivery fees updated.</AlertDescription>
              </Alert>
            )}
            {err && (
              <Alert variant="destructive" data-testid="settings-delivery-error">
                <AlertDescription>{err}</AlertDescription>
              </Alert>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {EMIRATES.map((e) => (
                <div key={e.key} className="space-y-2">
                  <Label htmlFor={`fee-${e.key}`}>{e.label} (AED)</Label>
                  <Input
                    id={`fee-${e.key}`}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={fees[e.key]}
                    onChange={(ev) =>
                      setFees((prev) => ({ ...prev, [e.key]: ev.target.value }))
                    }
                    data-testid={`settings-delivery-${e.key}`}
                  />
                </div>
              ))}
            </div>
            <Button type="submit" disabled={busy} data-testid="settings-delivery-save">
              {busy ? "Saving…" : "Save delivery fees"}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
